import { useEffect, useMemo, useRef, useState } from "react"
import { motion, useReducedMotion, AnimatePresence } from "framer-motion"
import { Menu, X, Download, Sparkles, ChevronDown } from "lucide-react"
import { CardinLogo } from "./cardin-logo"
import { Button } from "./ui/button"
import { LanguageSwitcher } from "./language-switcher"
import type { Locale } from "@/lib/i18n"

interface HeaderProps {
  locale: Locale
  onLocaleChange: (locale: Locale) => void
  translations: Record<string, any>
}

export function Header({ locale, onLocaleChange, translations }: HeaderProps) {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isVisible, setIsVisible] = useState(true)
  const [lastScrollY, setLastScrollY] = useState(0)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [activeId, setActiveId] = useState<string | null>(null)
  const prefersReducedMotion = useReducedMotion()
  const ticking = useRef(false)

  const navItems = useMemo(
    () => [
      { href: "#features", id: "features", label: translations.nav?.features || "Xususiyatlar" },
      { href: "#how", id: "how", label: translations.nav?.how || "Qanday ishlaydi" },
      { href: "#about", id: "about", label: translations.nav?.about || "Biz haqimizda" },
      { href: "#contact", id: "contact", label: translations.nav?.contact || "Aloqa" },
    ],
    [translations]
  )

  useEffect(() => {
    const onScroll = () => {
      if (ticking.current) return
      ticking.current = true
      requestAnimationFrame(() => {
        const y = window.scrollY
        setIsScrolled(y > 50)
        setIsVisible(!(y > lastScrollY && y > 150))
        setLastScrollY(y)
        ticking.current = false
      })
    }
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [lastScrollY])

  useEffect(() => {
    const sections = navItems
      .map((n) => document.querySelector<HTMLElement>(n.href))
      .filter(Boolean) as HTMLElement[]

    if (sections.length === 0) return

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)

        if (visible[0]?.target?.id) setActiveId(visible[0].target.id)
      },
      {
        rootMargin: "-30% 0px -55% 0px",
        threshold: [0.2, 0.4, 0.6, 0.8],
      }
    )

    sections.forEach((s) => observer.observe(s))
    return () => observer.disconnect()
  }, [navItems])

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden"
      return () => {
        document.body.style.overflow = ""
      }
    }
  }, [isMobileMenuOpen])

  const handleNavClick = (href: string) => (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    const el = document.querySelector(href)
    if (!el) return
    const y = el.getBoundingClientRect().top + window.scrollY - 100
    window.scrollTo({ top: y, behavior: prefersReducedMotion ? "auto" : "smooth" })
    setIsMobileMenuOpen(false)
  }

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${isVisible ? "translate-y-0" : "-translate-y-full"
          }`}
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className={`mt-4 sm:mt-6 rounded-2xl sm:rounded-3xl transition-all duration-500 ${isScrolled
                ? "bg-black/90 backdrop-blur-2xl shadow-[0_8px_40px_rgba(0,0,0,0.5)] border border-white/10"
                : "bg-black/70 backdrop-blur-xl shadow-[0_4px_24px_rgba(0,0,0,0.3)] border border-white/5"
              }`}
          >
            {/* Glow effect on top */}
            <div
              className={`absolute -top-px left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/50 to-transparent transition-opacity duration-500 ${isScrolled ? "opacity-100" : "opacity-0"
                }`}
            />

            <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4">
              {/* Logo with Text Animation */}
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="relative"
              >
                <a href="#" className="block">
                  {/* Logo Text */}
                  <div className="flex items-baseline gap-1.5">
                    {/* Cardin */}
                    <motion.span
                      className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-500 bg-clip-text text-transparent"
                      style={{
                        backgroundSize: "200% auto",
                      }}
                      animate={{
                        backgroundPosition: ["0% center", "200% center", "0% center"],
                      }}
                      transition={{
                        duration: 4,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                    >
                      Cardin
                    </motion.span>

                    {/* Taxi */}
                    <motion.span
                      className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-cyan-400 via-blue-400 to-cyan-500 bg-clip-text text-transparent"
                      style={{
                        backgroundSize: "200% auto",
                      }}
                      animate={{
                        backgroundPosition: ["200% center", "0% center", "200% center"],
                      }}
                      transition={{
                        duration: 4,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                    >
                      Taxi
                    </motion.span>
                  </div>
                </a>
              </motion.div>

              {/* Desktop Navigation */}
              <nav className="hidden lg:flex items-center gap-1">
                {navItems.map((item, idx) => {
                  const isActive = activeId === item.id
                  return (
                    <motion.a
                      key={item.href}
                      href={item.href}
                      onClick={handleNavClick(item.href)}
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.1, duration: 0.4 }}
                      className={`relative px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 ${isActive
                          ? "text-white bg-blue-600/20"
                          : "text-gray-400 hover:text-white hover:bg-white/10"
                        }`}
                    >
                      {item.label}
                      {/* Active underline */}
                      {isActive && (
                        <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-6 h-0.5 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full" />
                      )}
                    </motion.a>
                  )
                })}
              </nav>

              {/* Right Side Actions */}
              <div className="flex items-center gap-3">
                {/* Language Switcher */}
                <div className="relative z-[100]">
                  <LanguageSwitcher locale={locale} onLocaleChange={onLocaleChange} />
                </div>

                {/* Download Button - Desktop only */}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="hidden sm:flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white rounded-xl font-semibold text-sm shadow-lg shadow-blue-500/30 transition-all duration-300"
                >
                  <Download className="h-4 w-4" />
                  <span>Yuklab olish</span>
                </motion.button>

                {/* Mobile Menu Button */}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="lg:hidden p-2.5 rounded-xl bg-white/10 hover:bg-white/20 transition-all duration-300"
                  aria-label="Menu"
                >
                  <AnimatePresence mode="wait">
                    {isMobileMenuOpen ? (
                      <motion.div
                        key="close"
                        initial={{ rotate: -90, opacity: 0 }}
                        animate={{ rotate: 0, opacity: 1 }}
                        exit={{ rotate: 90, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <X className="h-5 w-5 text-white" />
                      </motion.div>
                    ) : (
                      <motion.div
                        key="menu"
                        initial={{ rotate: 90, opacity: 0 }}
                        animate={{ rotate: 0, opacity: 1 }}
                        exit={{ rotate: -90, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <Menu className="h-5 w-5 text-white" />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.button>
              </div>
            </div>
          </motion.div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-40 lg:hidden"
              onClick={() => setIsMobileMenuOpen(false)}
            />

            {/* Menu Panel */}
            <motion.div
              initial={{ opacity: 0, x: "100%" }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              className="fixed top-0 right-0 bottom-0 w-full sm:w-80 bg-gradient-to-b from-gray-900 to-black border-l border-white/10 z-50 lg:hidden overflow-y-auto"
            >
              {/* Close Button */}
              <div className="flex justify-end p-6">
                <motion.button
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="p-2 rounded-xl bg-white/10 hover:bg-white/20 transition-colors"
                >
                  <X className="h-6 w-6 text-white" />
                </motion.button>
              </div>

              {/* Navigation Links */}
              <nav className="px-6 space-y-2">
                {navItems.map((item, idx) => {
                  const isActive = activeId === item.id
                  return (
                    <motion.a
                      key={item.href}
                      href={item.href}
                      onClick={handleNavClick(item.href)}
                      initial={{ opacity: 0, x: 50 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.1 }}
                      className={`block px-4 py-3.5 rounded-xl text-base font-medium transition-all duration-300 ${isActive
                          ? "bg-blue-600/20 text-white border border-blue-500/30"
                          : "text-gray-300 hover:bg-white/10 hover:text-white"
                        }`}
                    >
                      <span className="flex items-center justify-between">
                        {item.label}
                        {isActive && (
                          <Sparkles className="h-4 w-4 text-blue-400" />
                        )}
                      </span>
                    </motion.a>
                  )
                })}
              </nav>

              {/* Download Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="px-6 mt-8 space-y-3"
              >
                <p className="text-sm text-gray-400 mb-4">Ilovani yuklab oling</p>

                <button className="w-full flex items-center justify-center gap-3 px-5 py-3.5 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white rounded-xl font-semibold transition-all duration-300 shadow-lg shadow-blue-500/30">
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.09l-.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                  </svg>
                  <span>App Store</span>
                </button>

                <button className="w-full flex items-center justify-center gap-3 px-5 py-3.5 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white rounded-xl font-semibold transition-all duration-300 shadow-lg shadow-emerald-500/30">
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 010 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.802 8.99l-2.303 2.303-8.635-8.635z" />
                  </svg>
                  <span>Google Play</span>
                </button>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  )
}